import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Drawer,
  Grid,
  Group,
  LoadingOverlay,
  Menu,
  Paper,
  Popover,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconDatabaseOff,
  IconEdit,
  IconInfoCircle,
  IconMap2,
  IconPlus,
} from '@tabler/icons-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeProvider.tsx';
import usePlace from '../hooks/places/usePlace.ts';
import { PlaceWithServices } from '../models/Place.ts';
import { getServiceColor } from '../const/service_type_mapping.ts';

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { getEVSEs, removeChargingService } from '../api/charging-service.ts';
import ImageDisplayCarousel from '../components/ui/places/ImageDisplayCarousel.tsx';
import { modals } from '@mantine/modals';
import { notifySuccess } from '../utils/NotifySuccess.tsx';
import usePublishPlace from '../hooks/places/usePublishPlace.ts';
import useUnpublishPlace from '../hooks/places/useUnpublishPlace.ts';
import { useViewportSize } from '@mantine/hooks';
import { usePlaceInformationPageModals } from '../hooks/ui/usePlaceInformationPageModals.ts';
import { AddEVSEAndConnectorsModal } from '../components/modal/places/AddEVSEAndConnectorsModal.tsx';
import { UpdateEVSEAndConnectorsModal } from '../components/modal/places/UpdateEVSEAndConnectorsModal.tsx';
import { AddChargingConnectorModal } from '../components/modal/places/AddChargingConnectorModal.tsx';
import { UpdateChargingServiceMetadataDrawer } from '../components/modal/places/UpdateChargingServiceMetadataDrawer.tsx';
import ChargingServiceInformationCard from '../components/ui/places/ChargingServiceInformationCard.tsx';
import { removeEVSE } from '../api/evse.ts';
import useDisableChargingService from '../hooks/charging-services/useDisableChargingService.ts';
import useDisableParkingService from '../hooks/parking-services/useDisableParkingService.ts';
import useDisableFuelingService from '../hooks/fueling-services/useDisableFuelingService.ts';
import FuelingServiceInformationCard from '../components/ui/places/FuelingServiceInformationCard.tsx';
import { removeFuelingService } from '../api/fueling-service.ts';
import ParkingServiceInformationCard from '../components/ui/places/ParkingServiceInformationCard.tsx';
import { removeParkingService } from '../api/parking-service.ts';
import { ServiceType } from '../models/ServiceType.ts';
import { UpdateParkingServiceMetadataDrawer } from '../components/modal/places/UpdateParkingServiceMetadataDrawer.tsx';
import { UpdateFuelingServiceMetadataDrawer } from '../components/modal/places/UpdateFuelingServiceMetadataDrawer.tsx';
import { ParkingService } from '../models/ParkingService.ts';
import { UpdatePlaceMetadataModal } from '../components/modal/places/UpdatePlaceMetadataModal.tsx';
import { AddChargingServiceModal } from '../components/modal/places/AddChargingServiceModal.tsx';
import { AddParkingServiceModal } from '../components/modal/places/AddParkingServiceModal.tsx';
import { AddFuelingServiceModal } from '../components/modal/places/AddFuelingServiceModal.tsx';
import { UpdateParkingServiceParkingInfoModal } from '../components/modal/places/UpdateParkingServiceParkingInfoModal.tsx';
import { ParkingRateOverviewModal } from '../components/modal/places/ParkingRateOverviewModal.tsx';
import { UpdateParkingServiceHeaderInfoModal } from '../components/modal/places/UpdateParkingServiceHeaderInfoModal.tsx';
import { UpdateFuelingServiceHeaderInfoModal } from '../components/modal/places/UpdateFuelingServiceHeaderInfoModal.tsx';
import { UpdateChargingServiceHeaderInfoModal } from '../components/modal/places/UpdateChargingServiceHeaderInfoModal.tsx';
import { UpdateFuelingServiceFuelingInfoModal } from '../components/modal/places/UpdateFuelingServiceFuelingInfoModal.tsx';
import { AddMaintenanceServiceModal } from '../components/modal/places/AddMaintenanceServiceModal.tsx';
import { AddInspectionServiceModal } from '../components/modal/places/AddInspectionServiceModal.tsx';
import { UpdateMaintenanceServiceHeaderInfoModal } from '../components/modal/places/UpdateMaintenanceServiceHeaderInfoModal.tsx';
import { UpdateInspectionServiceHeaderInfoModal } from '../components/modal/places/UpdateInspectionServiceHeaderInfoModal.tsx';
import { UpdateMaintenanceServiceMetadataDrawer } from '../components/modal/places/UpdateMaintenanceServiceMetadataDrawer.tsx';
import { UpdateInspectionServiceMetadataDrawer } from '../components/modal/places/UpdateInspectionServiceMetadataDrawer.tsx';
import MaintenanceServiceInformationCard from '../components/ui/places/MaintenanceServiceInformationCard.tsx';
import InspectionServiceInformationCard from '../components/ui/places/InspectionServiceInformationCard.tsx';
import useDisableMaintenanceService from '../hooks/maintenance-services/useDisableMaintenanceService.ts';
import useDisableInspectionService from '../hooks/inspection-services/useDisableInspectionService.ts';
import { removeMaintenanceService } from '../api/maintenance-service.ts';
import { removeInspectionService } from '../api/inspection-service.ts';

const PlaceInformationPage: React.FC = () => {
  const { id: placeId } = useParams();

  const { data: place, isLoading: isPlaceLoading } = usePlace(placeId);
  const { mutateAsync: publishPlace } = usePublishPlace();
  const { mutateAsync: unpublishPlace } = useUnpublishPlace();
  const { mutateAsync: disableChargingService } = useDisableChargingService();
  const { mutateAsync: disableParkingService } = useDisableParkingService();
  const { mutateAsync: disableFuelingService } = useDisableFuelingService();
  const { mutateAsync: disableMaintenanceService } =
    useDisableMaintenanceService();
  const { mutateAsync: disableInspectionService } =
    useDisableInspectionService();
  const disableServiceMap: Record<
    string,
    (id: string) => Promise<{ id: string }>
  > = {
    CHARGING: disableChargingService,
    PARKING: disableParkingService,
    FUELING: disableFuelingService,
    MAINTENANCE: disableMaintenanceService,
    INSPECTION: disableInspectionService,
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { height: viewportHeight } = useViewportSize();
  const queryClient = useQueryClient();

  // 圖片展示視窗是否被打開
  const [isServiceImageCarouselOpen, setIsServiceImageCarouselOpen] =
    useState(false);
  // 哪個服務的圖片視窗被打開
  const [selectedServiceIdForCarousel, setSelectedServiceIdForCarousel] =
    useState<string | null>(null);
  const [selectedServiceTypeForCarousel, setSelectedServiceTypeForCarousel] =
    useState<ServiceType | null>(null);
  // 哪個服務的資訊正在被編輯
  const [editingServiceMetadata, setEditingServiceMetadata] = useState<
    string | null
  >('');
  const [
    editingServiceMetadataServiceType,
    setEditingServiceMetadataServiceType,
  ] = useState<ServiceType | null>(null);
  // 當前操作的 serviceId
  const [operatingServiceId, setOperatingServiceId] = useState<string | null>(
    null,
  );
  // 當前操作的 evseId
  const [operatingEVSEId, setOperatingEVSEId] = useState<string | null>(null);
  // 場站的上下架 toggle 狀態
  const [checked, setChecked] = useState(false);

  const {
    addEVSEAndConnectorsModalOpened,
    openAddEVSEAndConnectorsModal,
    closeAddEVSEAndConnectorsModal,
    updateEVSEAndConnectorsModalOpened,
    openUpdateEVSEAndConnectorsModal,
    closeUpdateEVSEAndConnectorsModal,
    addChargingConnectorModalOpened,
    openAddChargingConnectorModal,
    closeAddChargingConnectorModal,
    updatePlaceMetadataModalOpened,
    openUpdatePlaceMetadataModal,
    closeUpdatePlaceMetadataModal,
    addChargingServiceModalOpened,
    openAddChargingServiceModal,
    closeAddChargingServiceModal,
    addParkingServiceModalOpened,
    openAddParkingServiceModal,
    closeAddParkingServiceModal,
    addFuelingServiceModalOpened,
    openAddFuelingServiceModal,
    closeAddFuelingServiceModal,
    updateParkingParkingInfoModalOpened,
    openUpdateParkingParkingInfoModal,
    closeUpdateParkingParkingInfoModal,
    parkingRateOverviewModalOpened,
    openParkingRateOverviewModalDisc,
    closeParkingRateOverviewModal,
    updateParkingHeaderInfoModalOpened,
    openUpdateParkingHeaderInfoModal,
    closeUpdateParkingHeaderInfoModal,
    updateFuelingHeaderInfoModalOpened,
    openUpdateFuelingHeaderInfoModal,
    closeUpdateFuelingHeaderInfoModal,
    updateChargingHeaderInfoModalOpened,
    openUpdateChargingHeaderInfoModal,
    closeUpdateChargingHeaderInfoModal,
    updateFuelingFuelingInfoModalOpened,
    openUpdateFuelingFuelingInfoModal,
    closeUpdateFuelingFuelingInfoModal,
    addMaintenanceServiceModalOpened,
    openAddMaintenanceServiceModal,
    closeAddMaintenanceServiceModal,
    addInspectionServiceModalOpened,
    openAddInspectionServiceModal,
    closeAddInspectionServiceModal,
    updateMaintenanceHeaderInfoModalOpened,
    openUpdateMaintenanceHeaderInfoModal,
    closeUpdateMaintenanceHeaderInfoModal,
    updateInspectionHeaderInfoModalOpened,
    openUpdateInspectionHeaderInfoModal,
    closeUpdateInspectionHeaderInfoModal,
  } = usePlaceInformationPageModals();

  ////////////////////////////////////////////////////////////////////////
  // Note: 接下來這段單純只是在計算出這個地點下有多少隻充電槍而已
  // Notes: 下一次更新 summary row 的內容後, 就可以拿掉了
  // 取得所有 CHARGING 類型的服務
  const chargingServices =
    place?.services.filter((service) => service.serviceType === 'CHARGING') ||
    [];
  const parkingServices =
    place?.services.filter((service) => service.serviceType === 'PARKING') ||
    [];
  const fuelingServices =
    place?.services.filter((service) => service.serviceType === 'FUELING') ||
    [];
  const maintenanceServices =
    place?.services.filter(
      (service) => service.serviceType === 'MAINTENANCE',
    ) || [];
  const inspectionServices =
    place?.services.filter((service) => service.serviceType === 'INSPECTION') ||
    [];

  // 使用 useQueries 為每個 charging service 取得其 EVSEs
  const evseQueries = useQueries({
    queries: chargingServices.map((service) => ({
      queryKey: ['chargingServices', service.id, 'evses'],
      queryFn: async () => {
        return await getEVSEs(service.id);
      },
      enabled: !!service.id,
    })),
  });

  // 整合所有 EVSEs 資料
  const allEVSEsData = evseQueries.map((query, index) => ({
    self: chargingServices[index],
    evses: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
  }));

  // 檢查是否所有 EVSE 查詢都完成
  const isEVSEsLoading = evseQueries.some((query) => query.isLoading);
  ////////////////////////////////////////////////////////////////////////

  // 處理 回到上一頁的按鈕
  const handleBackToMapManagementClick = () => {
    navigate('/places', { state: { selectedLocation: placeId } });
  };

  // 處理 切換場站上下架 toggle 的行為
  const handlePlaceStatusChange = async (
    prevValue: boolean,
    nextValue: boolean,
  ) => {
    if (prevValue !== nextValue) {
      if (nextValue) {
        await publishPlace(place.id);
        await queryClient.invalidateQueries({
          queryKey: ['places', place.id],
          exact: true,
        });
      } else {
        await unpublishPlace(place.id);
        for (const service of place.services) {
          await disableServiceMap[service.serviceType]?.(service.id);
          await queryClient.refetchQueries({
            queryKey: ['chargingServices', service.id],
            exact: true,
          });
        }
        await queryClient.refetchQueries({
          queryKey: ['places', place.id],
          exact: true,
        });
      }
    }
  };

  // 處理 編輯地點成功後的行為
  const handlePlaceMetadataUpdated = useCallback(async () => {
    closeUpdatePlaceMetadataModal();
    notifySuccess('更新地點資訊成功');
    await queryClient.invalidateQueries({
      queryKey: ['places', place.id],
      exact: true,
    });
  }, [closeUpdatePlaceMetadataModal, place?.id, queryClient]);

  // 處理 按下編輯地點 button 後的行為
  const handleEditPlaceMetadataClick = useCallback(() => {
    openUpdatePlaceMetadataModal();
  }, [openUpdatePlaceMetadataModal]);

  // 處理 新增充電服務成功後的行為
  const handleChargingServiceCreated = useCallback(async () => {
    closeAddChargingServiceModal();
    notifySuccess('新增充電服務成功');
    await queryClient.invalidateQueries({
      queryKey: ['places', place.id],
      exact: true,
    });
  }, [closeAddChargingServiceModal, place?.id, queryClient]);

  // 處理 新增保修服務成功後的行為
  const handleMaintenanceServiceCreated = useCallback(async () => {
    closeAddMaintenanceServiceModal();
    notifySuccess('新增保修服務成功');
    await queryClient.invalidateQueries({
      queryKey: ['places', place.id],
      exact: true,
    });
  }, [closeAddMaintenanceServiceModal, place?.id, queryClient]);

  // 處理 新增驗車服務成功後的行為
  const handleInspectionServiceCreated = useCallback(async () => {
    closeAddInspectionServiceModal();
    notifySuccess('新增驗車服務成功');
    await queryClient.invalidateQueries({
      queryKey: ['places', place.id],
      exact: true,
    });
  }, [closeAddInspectionServiceModal, place?.id, queryClient]);

  // 處理 按下新增保修服務 button 後的行為
  const handleCreateMaintenanceServiceClick = useCallback(() => {
    openAddMaintenanceServiceModal();
  }, [openAddMaintenanceServiceModal]);

  // 處理 按下新增驗車服務 button 後的行為
  const handleCreateInspectionServiceClick = useCallback(() => {
    openAddInspectionServiceModal();
  }, [openAddInspectionServiceModal]);

  // 處理 更新保修服務資訊成功後的行為
  const handleMaintenanceServiceMetadataUpdated = useCallback(async () => {
    setEditingServiceMetadataServiceType(null);
    await queryClient.invalidateQueries({
      queryKey: ['places', place.id],
      exact: true,
    });
  }, [place?.id, queryClient]);

  // 處理 更新驗車服務資訊成功後的行為
  const handleInspectionServiceMetadataUpdated = useCallback(async () => {
    setEditingServiceMetadataServiceType(null);
    await queryClient.invalidateQueries({
      queryKey: ['places', place.id],
      exact: true,
    });
  }, [place?.id, queryClient]);

  // 處理 按下編輯保修服務 button 後的行為
  const handleEditMaintenanceServiceMetadataClick = useCallback(
    (maintenanceServiceId: string) => {
      setEditingServiceMetadata(maintenanceServiceId);
      setEditingServiceMetadataServiceType('MAINTENANCE');
    },
    [],
  );

  // 處理 按下編輯驗車服務 button 後的行為
  const handleEditInspectionServiceMetadataClick = useCallback(
    (inspectionServiceId: string) => {
      setEditingServiceMetadata(inspectionServiceId);
      setEditingServiceMetadataServiceType('INSPECTION');
    },
    [],
  );

  // 處理 按下刪除保修服務 button 後的行為
  const handleDeleteMaintenanceServiceClick = useCallback(
    async (serviceId: string) => {
      await removeMaintenanceService(serviceId);
      notifySuccess('刪除保修服務成功');
      await queryClient.invalidateQueries({
        queryKey: ['places', place.id],
        exact: true,
      });
    },
    [place?.id, queryClient],
  );

  // 處理 按下刪除驗車服務 button 後的行為
  const handleDeleteInspectionServiceClick = useCallback(
    async (serviceId: string) => {
      await removeInspectionService(serviceId);
      notifySuccess('刪除驗車服務成功');
      await queryClient.invalidateQueries({
        queryKey: ['places', place.id],
        exact: true,
      });
    },
    [place?.id, queryClient],
  );

  // 處理 保修服務資訊更新成功後的行為
  const handleMaintenanceServiceHeaderInfoUpdated = useCallback(
    async (serviceId: string) => {
      closeUpdateMaintenanceHeaderInfoModal();
      notifySuccess('更新保修資訊成功');
      await queryClient.invalidateQueries({
        queryKey: ['maintenanceServices', serviceId],
        exact: true,
      });
    },
    [closeUpdateMaintenanceHeaderInfoModal, queryClient],
  );

  // 處理 驗車服務資訊更新成功後的行為
  const handleInspectionServiceHeaderInfoUpdated = useCallback(
    async (serviceId: string) => {
      closeUpdateInspectionHeaderInfoModal();
      notifySuccess('更新驗車資訊成功');
      await queryClient.invalidateQueries({
        queryKey: ['inspectionServices', serviceId],
        exact: true,
      });
    },
    [closeUpdateInspectionHeaderInfoModal, queryClient],
  );

  // 處理 按下編輯保修服務標題欄 button 後的行為
  const handleUpdateMaintenanceServiceHeaderInfoClick = useCallback(
    (serviceId: string) => {
      setOperatingServiceId(serviceId);
      openUpdateMaintenanceHeaderInfoModal();
    },
    [openUpdateMaintenanceHeaderInfoModal],
  );

  // 處理 按下編輯驗車服務標題欄 button 後的行為
  const handleUpdateInspectionServiceHeaderInfoClick = useCallback(
    (serviceId: string) => {
      setOperatingServiceId(serviceId);
      openUpdateInspectionHeaderInfoModal();
    },
    [openUpdateInspectionHeaderInfoModal],
  );

  // 處理 按下更新停車費率 button 後的行為
  const openParkingRateOverviewModal = useCallback(
    (serviceId: string) => {
      setOperatingServiceId(serviceId);
      openParkingRateOverviewModalDisc();
    },
    [openParkingRateOverviewModalDisc],
  );

  const handleUpdateParkingServiceParkingRateClick =
    openParkingRateOverviewModal;

  // 處理 編輯停車資訊成功後的行為
  const handleParkingServiceParkingInfoUpdated = useCallback(
    async (serviceId: string) => {
      closeUpdateParkingParkingInfoModal();
      notifySuccess('更新停車資訊成功');
      await queryClient.invalidateQueries({
        queryKey: ['parkingServices', serviceId],
        exact: true,
      });
      modals.openConfirmModal({
        title: (
          <Text style={{ fontSize: 16, fontWeight: 700 }}>前往費率管理</Text>
        ),
        children: (
          <Text style={{ fontSize: 14, fontWeight: 400 }}>
            是否繼續設定費率管理？
          </Text>
        ),
        centered: true,
        labels: { confirm: '是，繼續設定', cancel: '略過' },
        confirmProps: { color: 'blue' },

        onConfirm: () => handleUpdateParkingServiceParkingRateClick(serviceId),
      });
    },
    [
      closeUpdateParkingParkingInfoModal,
      handleUpdateParkingServiceParkingRateClick,
      queryClient,
    ],
  );

  // 處理 按下更新停車資訊 button 後的行為
  const handleUpdateParkingServiceParkingInfoClick = useCallback(
    (serviceId: string) => {
      setOperatingServiceId(serviceId);
      openUpdateParkingParkingInfoModal();
    },
    [openUpdateParkingParkingInfoModal],
  );

  // 處理 新增停車服務成功後的行為
  const handleParkingServiceCreated = useCallback(
    async (serviceId: string) => {
      closeAddParkingServiceModal();
      notifySuccess('新增停車服務成功');
      await queryClient.invalidateQueries({
        queryKey: ['places', place.id],
        exact: true,
      });
      handleUpdateParkingServiceParkingInfoClick(serviceId);
    },
    [
      closeAddParkingServiceModal,
      handleUpdateParkingServiceParkingInfoClick,
      place?.id,
      queryClient,
    ],
  );

  // 處理 按下更新加油資訊 button 後的行為
  const handleUpdateFuelingServiceFuelingInfoClick = useCallback(
    (serviceId: string) => {
      setOperatingServiceId(serviceId);
      openUpdateFuelingFuelingInfoModal();
    },
    [openUpdateFuelingFuelingInfoModal],
  );

  // 處理 新增加油服務成功後的行為
  const handleFuelingServiceCreated = useCallback(
    async (serviceId: string) => {
      closeAddFuelingServiceModal();
      notifySuccess('新增加油服務成功');
      await queryClient.invalidateQueries({
        queryKey: ['places', place.id],
        exact: true,
      });
      handleUpdateFuelingServiceFuelingInfoClick(serviceId);
    },
    [
      closeAddFuelingServiceModal,
      handleUpdateFuelingServiceFuelingInfoClick,
      place?.id,
      queryClient,
    ],
  );

  // 處理 按下新增充電服務 button 後的行為
  const handleCreateChargingServiceClick = useCallback(() => {
    openAddChargingServiceModal();
  }, [openAddChargingServiceModal]);

  // 處理 按下新增停車服務 button 後的行為
  const handleCreateParkingServiceClick = useCallback(() => {
    openAddParkingServiceModal();
  }, [openAddParkingServiceModal]);

  // 處理 按下新增加油服務 button 後的行為
  const handleCreateFuelingServiceClick = useCallback(() => {
    openAddFuelingServiceModal();
  }, [openAddFuelingServiceModal]);

  // 處理 更新充電服務資訊成功後的行為
  const handleChargingServiceMetadataUpdated = useCallback(async () => {
    setEditingServiceMetadataServiceType(null);
    await queryClient.invalidateQueries({
      queryKey: ['places', place.id],
      exact: true,
    });
  }, [place?.id, queryClient]);

  // 處理 更新停車服務資訊成功後的行為
  const handleParkingServiceMetadataUpdated = useCallback(async () => {
    setEditingServiceMetadataServiceType(null);
    await queryClient.invalidateQueries({
      queryKey: ['places', place.id],
      exact: true,
    });
  }, [place?.id, queryClient]);

  // 處理 更新加油服務資訊成功後的行為
  const handleFuelingServiceMetadataUpdated = useCallback(async () => {
    setEditingServiceMetadataServiceType(null);
    await queryClient.invalidateQueries({
      queryKey: ['places', place.id],
      exact: true,
    });
  }, [place?.id, queryClient]);

  // 處理 按下編輯充電服務 button 後的行為
  const handleEditChargingServiceMetadataClick = useCallback(
    (chargingServiceId: string) => {
      setEditingServiceMetadata(chargingServiceId);
      setEditingServiceMetadataServiceType('CHARGING');
    },
    [],
  );

  // 處理 按下編輯加油服務 button 後的行為
  const handleEditFuelingServiceMetadataClick = useCallback(
    (fuelingServiceId: string) => {
      setEditingServiceMetadata(fuelingServiceId);
      setEditingServiceMetadataServiceType('FUELING');
    },
    [],
  );

  // 處理 按下編輯停車服務 button 後的行為
  const handleEditParkingServiceMetadataClick = useCallback(
    (parkingServiceId: string) => {
      setEditingServiceMetadata(parkingServiceId);
      setEditingServiceMetadataServiceType('PARKING');
    },
    [],
  );

  // 處理 停車服務資訊更新成功後的行為
  const handleParkingServiceHeaderInfoUpdated = useCallback(
    async (serviceId: string) => {
      closeUpdateParkingHeaderInfoModal();
      notifySuccess('更新停車資訊成功');
      await queryClient.invalidateQueries({
        queryKey: ['parkingServices', serviceId],
        exact: true,
      });
    },
    [closeUpdateParkingHeaderInfoModal, queryClient],
  );

  // 處理 按下編輯停車服務標題欄 button 後的行為
  const handleUpdateParkingServiceHeaderInfoClick = useCallback(
    (serviceId: string) => {
      setOperatingServiceId(serviceId);
      openUpdateParkingHeaderInfoModal();
    },
    [openUpdateParkingHeaderInfoModal],
  );

  // 處理 加油服務資訊更新成功後的行為
  const handleFuelingServiceHeaderInfoUpdated = useCallback(
    async (serviceId: string) => {
      closeUpdateFuelingHeaderInfoModal();
      notifySuccess('更新加油資訊成功');
      await queryClient.invalidateQueries({
        queryKey: ['fuelingServices', serviceId],
        exact: false,
      });
    },
    [closeUpdateFuelingHeaderInfoModal, queryClient],
  );

  // 處理 按下編輯加油服務標題欄 button 後的行為
  const handleUpdateFuelingServiceHeaderInfoClick = useCallback(
    (serviceId: string) => {
      setOperatingServiceId(serviceId);
      openUpdateFuelingHeaderInfoModal();
    },
    [openUpdateFuelingHeaderInfoModal],
  );

  // 處理 充電服務資訊更新成功後的行為
  const handleChargingServiceHeaderInfoUpdated = useCallback(
    async (serviceId: string) => {
      closeUpdateChargingHeaderInfoModal();
      notifySuccess('更新充電資訊成功');
      await queryClient.invalidateQueries({
        queryKey: ['chargingServices', serviceId],
        exact: true,
      });
    },
    [closeUpdateChargingHeaderInfoModal, queryClient],
  );

  // 處理 按下編輯充電服務標題欄 button 後的行為
  const handleUpdateChargingServiceHeaderInfoClick = useCallback(
    (serviceId: string) => {
      setOperatingServiceId(serviceId);
      openUpdateChargingHeaderInfoModal();
    },
    [openUpdateChargingHeaderInfoModal],
  );

  // 處理 充電服務(樁&槍)新增成功後的行為
  const handleEVSEAndConnectorsAdded = useCallback(
    async (serviceId: string) => {
      await queryClient.invalidateQueries({
        queryKey: ['chargingServices', serviceId, 'evses'],
        exact: true,
      });
      closeAddEVSEAndConnectorsModal();
    },
    [closeAddEVSEAndConnectorsModal, queryClient],
  );

  // 處理 按下新增充電資訊(樁&槍) button 後的行為
  const handleAddEVSEAndConnectorsClick = useCallback(
    (serviceId: string) => {
      setOperatingServiceId(serviceId);
      openAddEVSEAndConnectorsModal();
    },
    [openAddEVSEAndConnectorsModal],
  );

  // 處理 充電服務(樁&槍)更新成功後的行為
  const handleEVSEAndConnectorsUpdated = useCallback(
    async (serviceId: string) => {
      await queryClient.invalidateQueries({
        queryKey: ['chargingServices', serviceId, 'evses'],
        exact: true,
      });
      closeUpdateEVSEAndConnectorsModal();
    },
    [closeUpdateEVSEAndConnectorsModal, queryClient],
  );

  // 處理 按下更新充電資訊(樁&槍) button 後的行為
  const handleUpdateEVSEAndConnectorsClick = useCallback(
    (serviceId: string, evseId: string) => {
      setOperatingServiceId(serviceId);
      setOperatingEVSEId(evseId);
      openUpdateEVSEAndConnectorsModal();
    },
    [openUpdateEVSEAndConnectorsModal],
  );

  // 處理 編輯加油資訊成功後的行為
  const handleFuelingServiceFuelingInfoUpdated = useCallback(
    async (serviceId: string) => {
      closeUpdateFuelingFuelingInfoModal();
      notifySuccess('更新加油資訊成功');
      await queryClient.invalidateQueries({
        queryKey: ['fuelingServices', serviceId],
        exact: true,
      });
    },
    [closeUpdateFuelingFuelingInfoModal, queryClient],
  );

  // 處理 按下刪除充電資訊(樁&槍) button 後的行為
  const handleDeleteEVSEAndConnectorsClick = useCallback(
    async (serviceId: string, evseId: string) => {
      await removeEVSE(evseId);
      notifySuccess('刪除充電資訊成功');
      await queryClient.invalidateQueries({
        queryKey: ['chargingServices', serviceId, 'evses'],
        exact: false,
      });
    },
    [queryClient],
  );

  // 處理 充電槍新增成功後的行為
  const handleChargingConnectorAdded = useCallback(
    async (serviceId: string) => {
      await queryClient.invalidateQueries({
        queryKey: ['chargingServices', serviceId, 'evses'],
        exact: false,
      });

      closeAddChargingConnectorModal();
    },
    [closeAddChargingConnectorModal, queryClient],
  );

  // 處理 按下刪除充電服務 button 後的行為
  const handleDeleteChargingServiceClick = useCallback(
    async (serviceId: string) => {
      await removeChargingService(serviceId);
      notifySuccess('刪除充電服務成功');
      await queryClient.invalidateQueries({
        queryKey: ['places', place.id],
        exact: true,
      });
    },
    [place?.id, queryClient],
  );

  // 處理 按下刪除加油服務 button 後的行為
  const handleDeleteFuelingServiceClick = useCallback(
    async (serviceId: string) => {
      await removeFuelingService(serviceId);
      notifySuccess('刪除加油服務成功');
      await queryClient.invalidateQueries({
        queryKey: ['places', place.id],
        exact: true,
      });
    },
    [place?.id, queryClient],
  );

  // 處理 按下刪除停車服務 button 後的行為
  const handleDeleteParkingServiceClick = useCallback(
    async (serviceId: string) => {
      await removeParkingService(serviceId);
      notifySuccess('刪除停車服務成功');
      await queryClient.invalidateQueries({
        queryKey: ['places', place.id],
        exact: true,
      });
    },
    [place?.id, queryClient],
  );

  // 處理 按下新增充電槍 button 後的行為
  const handleAddChargingConnectorClick = useCallback(
    (serviceId: string, evseId: string) => {
      setOperatingServiceId(serviceId);
      setOperatingEVSEId(evseId);
      openAddChargingConnectorModal();
    },
    [openAddChargingConnectorModal],
  );

  // 從費率編輯頁返回時，自動重新開啟費率管理 Modal
  // hasOpenedRateModalRef: 防止 React StrictMode 的 double-invoke 開兩次 modal
  const hasOpenedRateModalRef = useRef(false);
  useEffect(() => {
    if (location.state?.openRateOverview) {
      if (hasOpenedRateModalRef.current) return;
      hasOpenedRateModalRef.current = true;
      const serviceId = location.state?.serviceId as string | undefined;
      navigate('.', { replace: true, state: {} });
      if (serviceId) {
        openParkingRateOverviewModal(serviceId);
      }
    } else {
      // state 被清除後重置，下次返回時才能再次開啟
      hasOpenedRateModalRef.current = false;
    }
  }, [location.state, navigate, openParkingRateOverviewModal]);

  // 處理 場站上下架狀態的即時更新
  useEffect(() => {
    if (!isPlaceLoading && !isEVSEsLoading && place) {
      setChecked(place.publishStatus === 'PUBLISHED');
    }
  }, [isPlaceLoading, isEVSEsLoading, place?.publishStatus, place]);

  const { colorScheme } = useTheme();

  if (isPlaceLoading || isEVSEsLoading) {
    return (
      <>
        <LoadingOverlay />
      </>
    );
  }

  // 取得 地點之下所有服務類型的數量
  const getNumOfPlaceServices = (place: PlaceWithServices) => {
    const count = {
      CHARGING: 0,
      PARKING: 0,
      FUELING: 0,
      MAINTENANCE: 0,
      INSPECTION: 0,
    };

    place.services.forEach((service) => {
      if (count[service.serviceType] !== undefined)
        count[service.serviceType]++;
    });

    return {
      numOfCharging: count.CHARGING,
      numOfParking: count.PARKING,
      numOfFueling: count.FUELING,
      numOfMaintenance: count.MAINTENANCE,
      numOfInspection: count.INSPECTION,
    };
  };

  const numOfPlaceServices = getNumOfPlaceServices(place);

  // 取得 地點之下所有充電槍的數量
  const getNumOfConnectors = () => {
    let count = 0;
    allEVSEsData.forEach((service) => {
      service.evses.forEach((evse) => {
        count += Object.values(evse.connectors).length;
      });
    });

    return count;
  };

  const getNumOfParkingSpaces = () => {
    return parkingServices.reduce((acc, service) => {
      return (
        acc + ((service as ParkingService).parkingSpaceInfo.totalSpacesSum ?? 0)
      );
    }, 0);
  };

  const numOfConnectors = getNumOfConnectors();

  const numOfParkingSpaces = getNumOfParkingSpaces();

  return (
    <>
      <Drawer
        opened={!!editingServiceMetadataServiceType}
        closeOnClickOutside={false}
        onClose={() => setEditingServiceMetadataServiceType(null)}
        title={
          editingServiceMetadataServiceType === 'CHARGING'
            ? '編輯充電服務'
            : editingServiceMetadataServiceType === 'PARKING'
              ? '編輯停車服務'
              : editingServiceMetadataServiceType === 'FUELING'
                ? '編輯加油服務'
                : editingServiceMetadataServiceType === 'MAINTENANCE'
                  ? '編輯保修服務'
                  : editingServiceMetadataServiceType === 'INSPECTION'
                    ? '編輯驗車服務'
                    : '編輯服務'
        }
        position="right"
        size="659px"
        padding="0px"
        styles={{
          title: {
            fontSize: 16,
            fontWeight: 700,
            lineHeight: '24px',
          },
          header: {
            height: 38,
            paddingTop: 0,
            paddingBottom: 0,
            paddingInline: 16,
            display: 'flex',
            alignItems: 'center',
          },
          body: {
            padding: 0,
            height: 'calc(100vh - 60px)',
            overflow: 'hidden',
          },
        }}
      >
        {editingServiceMetadata &&
          editingServiceMetadataServiceType === 'CHARGING' && (
            <UpdateChargingServiceMetadataDrawer
              placeId={place.id}
              chargingServiceId={editingServiceMetadata}
              onSuccess={handleChargingServiceMetadataUpdated}
              onCancel={() => {
                setEditingServiceMetadataServiceType(null);
                setEditingServiceMetadata(null);
              }}
            />
          )}
        {editingServiceMetadata &&
          editingServiceMetadataServiceType === 'PARKING' && (
            <UpdateParkingServiceMetadataDrawer
              placeId={place.id}
              parkingServiceId={editingServiceMetadata}
              onSuccess={handleParkingServiceMetadataUpdated}
              onCancel={() => {
                setEditingServiceMetadataServiceType(null);
                setEditingServiceMetadata(null);
              }}
            />
          )}
        {editingServiceMetadata &&
          editingServiceMetadataServiceType === 'FUELING' && (
            <UpdateFuelingServiceMetadataDrawer
              placeId={place.id}
              fuelingServiceId={editingServiceMetadata}
              onSuccess={handleFuelingServiceMetadataUpdated}
              onCancel={() => {
                setEditingServiceMetadataServiceType(null);
                setEditingServiceMetadata(null);
              }}
            />
          )}
        {editingServiceMetadata &&
          editingServiceMetadataServiceType === 'MAINTENANCE' && (
            <UpdateMaintenanceServiceMetadataDrawer
              placeId={place.id}
              maintenanceServiceId={editingServiceMetadata}
              onSuccess={handleMaintenanceServiceMetadataUpdated}
              onCancel={() => {
                setEditingServiceMetadataServiceType(null);
                setEditingServiceMetadata(null);
              }}
            />
          )}
        {editingServiceMetadata &&
          editingServiceMetadataServiceType === 'INSPECTION' && (
            <UpdateInspectionServiceMetadataDrawer
              placeId={place.id}
              inspectionServiceId={editingServiceMetadata}
              onSuccess={handleInspectionServiceMetadataUpdated}
              onCancel={() => {
                setEditingServiceMetadataServiceType(null);
                setEditingServiceMetadata(null);
              }}
            />
          )}
      </Drawer>

      {addEVSEAndConnectorsModalOpened && (
        <AddEVSEAndConnectorsModal
          opened={addEVSEAndConnectorsModalOpened}
          onClose={closeAddEVSEAndConnectorsModal}
          serviceId={operatingServiceId}
          onSuccess={() => handleEVSEAndConnectorsAdded(operatingServiceId)}
        />
      )}

      {updateEVSEAndConnectorsModalOpened && (
        <UpdateEVSEAndConnectorsModal
          opened={updateEVSEAndConnectorsModalOpened}
          onClose={closeUpdateEVSEAndConnectorsModal}
          serviceId={operatingServiceId}
          evseId={operatingEVSEId}
          onSuccess={() => handleEVSEAndConnectorsUpdated(operatingServiceId)}
        />
      )}

      {addChargingConnectorModalOpened && (
        <AddChargingConnectorModal
          opened={addChargingConnectorModalOpened}
          onClose={closeAddChargingConnectorModal}
          serviceId={operatingServiceId}
          evseId={operatingEVSEId}
          onSuccess={() => handleChargingConnectorAdded(operatingServiceId)}
        />
      )}

      {updatePlaceMetadataModalOpened && (
        <UpdatePlaceMetadataModal
          opened={updatePlaceMetadataModalOpened}
          onClose={closeUpdatePlaceMetadataModal}
          place={place}
          onSuccess={handlePlaceMetadataUpdated}
        />
      )}

      {addChargingServiceModalOpened && (
        <AddChargingServiceModal
          opened={addChargingServiceModalOpened}
          onClose={closeAddChargingServiceModal}
          placeId={place.id}
          placeName={place.name}
          onSuccess={handleChargingServiceCreated}
        />
      )}

      {addParkingServiceModalOpened && (
        <AddParkingServiceModal
          opened={addParkingServiceModalOpened}
          onClose={closeAddParkingServiceModal}
          placeId={place.id}
          placeName={place.name}
          onSuccess={handleParkingServiceCreated}
        />
      )}

      {addFuelingServiceModalOpened && (
        <AddFuelingServiceModal
          opened={addFuelingServiceModalOpened}
          onClose={closeAddFuelingServiceModal}
          placeId={place.id}
          placeName={place.name}
          onSuccess={handleFuelingServiceCreated}
        />
      )}

      {addMaintenanceServiceModalOpened && (
        <AddMaintenanceServiceModal
          opened={addMaintenanceServiceModalOpened}
          onClose={closeAddMaintenanceServiceModal}
          placeId={place.id}
          placeName={place.name}
          onSuccess={handleMaintenanceServiceCreated}
        />
      )}

      {addInspectionServiceModalOpened && (
        <AddInspectionServiceModal
          opened={addInspectionServiceModalOpened}
          onClose={closeAddInspectionServiceModal}
          placeId={place.id}
          placeName={place.name}
          onSuccess={handleInspectionServiceCreated}
        />
      )}

      {updateParkingParkingInfoModalOpened && (
        <UpdateParkingServiceParkingInfoModal
          opened={updateParkingParkingInfoModalOpened}
          onClose={closeUpdateParkingParkingInfoModal}
          serviceId={operatingServiceId}
          onSuccess={() =>
            handleParkingServiceParkingInfoUpdated(operatingServiceId)
          }
        />
      )}

      {parkingRateOverviewModalOpened && (
        <ParkingRateOverviewModal
          opened={parkingRateOverviewModalOpened}
          onClose={closeParkingRateOverviewModal}
          serviceId={operatingServiceId}
          onNavigateToEdit={(ruleIndex, ruleType) => {
            navigate(
              `/parking-services/${operatingServiceId}/parking-rates/${ruleType}/${ruleIndex}/edit`,
              { state: { placeId, serviceId: operatingServiceId } },
            );
          }}
          onNavigateToAdd={() => {
            navigate(
              `/parking-services/${operatingServiceId}/parking-rates/new`,
              {
                state: { placeId, serviceId: operatingServiceId },
              },
            );
          }}
          onNavigateToCopy={(rule, ruleType) => {
            navigate(
              `/parking-services/${operatingServiceId}/parking-rates/new`,
              {
                state: {
                  placeId,
                  serviceId: operatingServiceId,
                  copyRule: rule,
                  copyRuleType: ruleType,
                },
              },
            );
          }}
        />
      )}

      {updateParkingHeaderInfoModalOpened && (
        <UpdateParkingServiceHeaderInfoModal
          opened={updateParkingHeaderInfoModalOpened}
          onClose={closeUpdateParkingHeaderInfoModal}
          serviceId={operatingServiceId}
          onSuccess={() =>
            handleParkingServiceHeaderInfoUpdated(operatingServiceId)
          }
        />
      )}

      {updateFuelingHeaderInfoModalOpened && (
        <UpdateFuelingServiceHeaderInfoModal
          opened={updateFuelingHeaderInfoModalOpened}
          onClose={closeUpdateFuelingHeaderInfoModal}
          serviceId={operatingServiceId}
          onSuccess={() =>
            handleFuelingServiceHeaderInfoUpdated(operatingServiceId)
          }
        />
      )}

      {updateChargingHeaderInfoModalOpened && (
        <UpdateChargingServiceHeaderInfoModal
          opened={updateChargingHeaderInfoModalOpened}
          onClose={closeUpdateChargingHeaderInfoModal}
          serviceId={operatingServiceId}
          onSuccess={() =>
            handleChargingServiceHeaderInfoUpdated(operatingServiceId)
          }
        />
      )}

      {updateMaintenanceHeaderInfoModalOpened && (
        <UpdateMaintenanceServiceHeaderInfoModal
          opened={updateMaintenanceHeaderInfoModalOpened}
          onClose={closeUpdateMaintenanceHeaderInfoModal}
          serviceId={operatingServiceId}
          onSuccess={() =>
            handleMaintenanceServiceHeaderInfoUpdated(operatingServiceId)
          }
        />
      )}

      {updateInspectionHeaderInfoModalOpened && (
        <UpdateInspectionServiceHeaderInfoModal
          opened={updateInspectionHeaderInfoModalOpened}
          onClose={closeUpdateInspectionHeaderInfoModal}
          serviceId={operatingServiceId}
          onSuccess={() =>
            handleInspectionServiceHeaderInfoUpdated(operatingServiceId)
          }
        />
      )}

      {updateFuelingFuelingInfoModalOpened && (
        <UpdateFuelingServiceFuelingInfoModal
          opened={updateFuelingFuelingInfoModalOpened}
          onClose={closeUpdateFuelingFuelingInfoModal}
          serviceId={operatingServiceId}
          onSuccess={() =>
            handleFuelingServiceFuelingInfoUpdated(operatingServiceId)
          }
        />
      )}

      {selectedServiceIdForCarousel &&
        selectedServiceTypeForCarousel &&
        isServiceImageCarouselOpen && (
          <ImageDisplayCarousel
            key={selectedServiceIdForCarousel}
            serviceId={selectedServiceIdForCarousel}
            serviceType={selectedServiceTypeForCarousel}
            isServiceImageCarouselOpen={true}
            setIsServiceImageCarouselOpen={(isOpen) => {
              if (!isOpen) {
                setSelectedServiceIdForCarousel(null);
                setSelectedServiceTypeForCarousel(null);
              }
            }}
          />
        )}

      <div
        style={{
          minWidth: 1166,
        }}
      >
        {/* Header */}
        <Stack
          gap={12}
          style={{
            alignItems: 'start',
            paddingInline: 0,
            position: 'sticky',
            top: 20,
          }}
        >
          {/* 返回圖資管理按鈕 */}
          <Button
            variant={'transparent'}
            leftSection={<IconChevronLeft size={16} />}
            onClick={handleBackToMapManagementClick}
            style={{ color: 'grey', height: 32, fontSize: 14, fontWeight: 400 }}
            styles={{ root: { paddingInline: 0 } }}
          >
            返回圖資管理
          </Button>

          {/* 場站層級資訊 */}
          <Card
            shadow={'xs'}
            style={{
              paddingInline: 30,
              height: 108,
              width: '100%',
              backgroundColor:
                colorScheme === 'light' ? 'white' : 'var(--mantine-color-body)',
              borderRadius: 16,
              marginBottom: 20,
            }}
          >
            <Group
              align={'center'}
              style={{ height: 76, justifyContent: 'space-between' }}
            >
              <Group style={{ width: 850 }}>
                <Stack gap={8} align={'center'} style={{ width: 52 }}>
                  {/* 上下架按鈕 */}
                  <Switch
                    size={'md'}
                    style={{ paddingTop: 4 }}
                    checked={checked}
                    onChange={(event) => {
                      const prev = checked;
                      const next = event.currentTarget.checked;
                      modals.openConfirmModal({
                        title: next ? (
                          <Text style={{ fontSize: 16, fontWeight: 700 }}>
                            上架地點
                          </Text>
                        ) : (
                          <Text style={{ fontSize: 16, fontWeight: 700 }}>
                            下架地點
                          </Text>
                        ),
                        children: next ? (
                          <Text style={{ fontSize: 14, fontWeight: 400 }}>
                            是否確認上架{place.name}？
                          </Text>
                        ) : (
                          <Text style={{ fontSize: 14, fontWeight: 400 }}>
                            是否確認下架{place.name}？
                          </Text>
                        ),
                        centered: true,
                        labels: { confirm: '確認', cancel: '取消' },
                        confirmProps: { color: 'blue' },

                        onCancel: () => {
                          setChecked(prev);
                        },

                        onConfirm: async () => {
                          setChecked(next);
                          await handlePlaceStatusChange(prev, next);
                        },
                      });
                    }}
                  />
                  <Text
                    style={{
                      height: 18,
                      fontSize: 12,
                      color:
                        place.publishStatus === 'PUBLISHED'
                          ? '#228be6'
                          : '#b8b8b8',
                    }}
                  >
                    {place.publishStatus === 'PUBLISHED'
                      ? '已上架'
                      : place.publishStatus === 'UNPUBLISHED'
                        ? '已下架'
                        : '待上架'}
                  </Text>
                </Stack>
                <Stack gap={0} style={{ width: 782 }}>
                  {/* 場站名稱 與 小編精選 */}
                  <Group style={{ height: 32, alignContent: 'center' }}>
                    <Text style={{ fontSize: 24, fontWeight: 700, height: 32 }}>
                      {place.name}
                    </Text>
                    <Popover
                      variant={'light'}
                      shadow={'xs'}
                      position={'right'}
                      withArrow
                    >
                      <Popover.Target>
                        <ActionIcon
                          variant={'light'}
                          size={'sm'}
                          style={{ height: 20, width: 20 }}
                        >
                          <IconInfoCircle style={{ height: 16, width: 16 }} />
                        </ActionIcon>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <Stack style={{ width: 243 }}>
                          <Text style={{ fontSize: 12, fontWeight: 700 }}>
                            小編精選
                          </Text>
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: 400,
                              whiteSpace: 'pre-wrap',
                              maxHeight: 75,
                              overflow: 'scroll',
                            }}
                          >
                            {place.notes}
                          </Text>
                        </Stack>
                      </Popover.Dropdown>
                    </Popover>
                  </Group>
                  {/*地址, 經緯度與街景*/}
                  <Group gap={0} style={{ height: 32 }}>
                    {/* 地址 & 經緯度 */}
                    <Tooltip
                      label={place.address}
                      position={'bottom'}
                      offset={0}
                      openDelay={200}
                      color={'rgba(120, 120, 120, 0.85)'}
                      style={{
                        fontSize: 12,
                      }}
                    >
                      <Text
                        style={{
                          color: '#868E96',
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: '20px',
                          maxWidth: 450,
                          maxHeight: 32,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          flexShrink: 1,
                        }}
                      >
                        {place.address}
                      </Text>
                    </Tooltip>
                    <Text
                      style={{
                        color: '#868E96',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      {'｜'}
                      {place.googleStreetViewInfo.lat}
                      {', '}
                      {place.googleStreetViewInfo.lng}
                    </Text>
                    {/* Google 街景 */}
                    <Button
                      component={'a'}
                      href={place.googleStreetViewInfo.url}
                      target={'_blank'}
                      variant={'transparent'}
                      leftSection={<IconMap2 />}
                    >
                      Google 街景
                    </Button>
                  </Group>
                </Stack>
              </Group>
              {/* 編輯場站資訊 */}
              <Group>
                <Menu
                  styles={{
                    dropdown: {
                      width: 124,
                      overflow: 'scroll',
                    },
                  }}
                >
                  <Menu.Target>
                    <Button leftSection={<IconPlus />}>新增服務</Button>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Item onClick={handleCreateParkingServiceClick}>
                      停車
                    </Menu.Item>
                    <Menu.Item onClick={handleCreateChargingServiceClick}>
                      充電
                    </Menu.Item>
                    <Menu.Item onClick={handleCreateFuelingServiceClick}>
                      加油
                    </Menu.Item>
                    <Menu.Item onClick={handleCreateMaintenanceServiceClick}>
                      保修
                    </Menu.Item>
                    <Menu.Item onClick={handleCreateInspectionServiceClick}>
                      驗車
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>

                <Button
                  variant={'light'}
                  leftSection={<IconEdit />}
                  onClick={handleEditPlaceMetadataClick}
                >
                  編輯
                </Button>
              </Group>
            </Group>
          </Card>
        </Stack>

        {/* 可滾動內容區 */}
        <div
          style={{
            flex: 1,
            height: viewportHeight - 212,
            overflowX: 'hidden',
            overflowY: 'auto',
            border: 0,
          }}
        >
          <Grid justify={'space-between'}>
            <Grid.Col span={3}>
              {/* 服務類型/數量 */}
              <Card
                shadow={'xs'}
                style={{
                  paddingInline: 30,
                  paddingTop: 24,
                  paddingBottom: 24,
                  height: 98,
                  backgroundColor:
                    colorScheme === 'light'
                      ? 'white'
                      : 'var(--mantine-color-body)',
                  borderRadius: 16,
                  marginBottom: 20,
                  overflow: 'scroll',
                }}
              >
                <Text
                  style={{
                    lineHeight: '14px',
                    paddingBottom: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#868E96',
                  }}
                >
                  服務類型/數量
                </Text>
                <div
                  style={{
                    lineHeight: '32px',
                    paddingTop: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#868E96',
                  }}
                >
                  {place.services.length ? (
                    <Group>
                      {numOfPlaceServices.numOfParking && (
                        <Badge
                          style={{
                            backgroundColor: getServiceColor('PARKING'),
                            color: 'black',
                          }}
                        >
                          停車: {numOfPlaceServices.numOfParking}
                        </Badge>
                      )}
                      {numOfPlaceServices.numOfCharging && (
                        <Badge
                          style={{
                            backgroundColor: getServiceColor('CHARGING'),
                            color: 'black',
                          }}
                        >
                          充電: {numOfPlaceServices.numOfCharging}
                        </Badge>
                      )}
                      {numOfPlaceServices.numOfFueling && (
                        <Badge
                          style={{
                            backgroundColor: getServiceColor('FUELING'),
                            color: 'black',
                          }}
                        >
                          加油: {numOfPlaceServices.numOfFueling}
                        </Badge>
                      )}
                      {numOfPlaceServices.numOfMaintenance && (
                        <Badge
                          style={{
                            backgroundColor: getServiceColor('MAINTENANCE'),
                            color: 'black',
                          }}
                        >
                          保修: {numOfPlaceServices.numOfMaintenance}
                        </Badge>
                      )}
                      {numOfPlaceServices.numOfInspection && (
                        <Badge
                          style={{
                            backgroundColor: getServiceColor('INSPECTION'),
                            color: 'black',
                          }}
                        >
                          驗車: {numOfPlaceServices.numOfInspection}
                        </Badge>
                      )}
                    </Group>
                  ) : (
                    '-'
                  )}
                </div>
              </Card>
            </Grid.Col>
            <Grid.Col span={3}>
              {/* 總車位數 */}
              <Card
                shadow={'xs'}
                style={{
                  paddingInline: 30,
                  paddingTop: 24,
                  paddingBottom: 24,
                  height: 98,
                  backgroundColor:
                    colorScheme === 'light'
                      ? 'white'
                      : 'var(--mantine-color-body)',
                  borderRadius: 16,
                  marginBottom: 20,
                }}
              >
                <Text
                  style={{
                    lineHeight: '14px',
                    paddingBottom: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#868E96',
                  }}
                >
                  總車位數
                </Text>
                <Text
                  style={{
                    lineHeight: '32px',
                    fontSize: 20,
                    fontWeight: 400,
                    color: colorScheme === 'light' ? 'black' : 'white',
                  }}
                >
                  {place.services.length ? numOfParkingSpaces : '-'}
                </Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={3}>
              {/* 總充電槍數 */}
              <Card
                shadow={'xs'}
                style={{
                  paddingInline: 30,
                  paddingTop: 24,
                  paddingBottom: 24,
                  height: 98,
                  backgroundColor:
                    colorScheme === 'light'
                      ? 'white'
                      : 'var(--mantine-color-body)',
                  borderRadius: 16,
                  marginBottom: 20,
                }}
              >
                <Text
                  style={{
                    lineHeight: '14px',
                    paddingBottom: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#868E96',
                  }}
                >
                  總充電槍數
                </Text>
                <Text
                  style={{
                    lineHeight: '32px',
                    fontSize: 20,
                    fontWeight: 400,
                    color: colorScheme === 'light' ? 'black' : 'white',
                  }}
                >
                  {numOfConnectors}
                </Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={3}>
              {/* 最新更新時間 */}
              <Card
                shadow={'xs'}
                style={{
                  paddingInline: 30,
                  paddingTop: 24,
                  paddingBottom: 24,
                  height: 98,
                  backgroundColor:
                    colorScheme === 'light'
                      ? 'white'
                      : 'var(--mantine-color-body)',
                  borderRadius: 16,
                  marginBottom: 20,
                }}
              >
                <Text
                  style={{
                    lineHeight: '18px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#868E96',
                  }}
                >
                  最新更新時間
                </Text>
                <Text
                  style={{
                    lineHeight: '32px',
                    fontSize: 20,
                    fontWeight: 400,
                    color: '#228BE6',
                  }}
                >
                  -
                </Text>
              </Card>
            </Grid.Col>
          </Grid>

          {/* 服務區塊 */}
          {/* 若 尚沒有服務, 顯示新增 */}
          {!place.services.length && (
            <Paper
              shadow={'xs'}
              style={{
                paddingInline: 30,
                height: 480,
                backgroundColor:
                  colorScheme === 'light'
                    ? 'white'
                    : 'var(--mantine-color-body)',
                borderRadius: 16,
                marginBottom: 20,
              }}
            >
              <Stack
                style={{ position: 'relative', top: 175, alignItems: 'center' }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#E9ECEF',
                  }}
                >
                  <IconDatabaseOff
                    style={{
                      position: 'relative',
                      top: 8,
                      left: 8,
                      height: 24,
                      width: 24,
                      color: '#868E96',
                    }}
                  />
                </div>

                <Text
                  style={{ fontSize: 14, fontWeight: 400, color: '#868E96' }}
                >
                  目前還沒有任何服務，點擊「新增服務」開始建立！
                </Text>
                <Menu
                  styles={{
                    dropdown: {
                      width: 124,
                      overflow: 'scroll',
                    },
                  }}
                >
                  <Menu.Target>
                    <Button leftSection={<IconPlus />}>新增服務</Button>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Item onClick={handleCreateParkingServiceClick}>
                      停車
                    </Menu.Item>
                    <Menu.Item onClick={handleCreateChargingServiceClick}>
                      充電
                    </Menu.Item>
                    <Menu.Item onClick={handleCreateFuelingServiceClick}>
                      加油
                    </Menu.Item>
                    <Menu.Item onClick={handleCreateMaintenanceServiceClick}>
                      保修
                    </Menu.Item>
                    <Menu.Item onClick={handleCreateInspectionServiceClick}>
                      驗車
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Stack>
            </Paper>
          )}

          {/* 若 已有服務, 顯示資訊 */}
          {parkingServices.length > 0 &&
            parkingServices.map((service) => {
              return (
                <ParkingServiceInformationCard
                  serviceId={service.id}
                  setIsServiceImageCarouselOpen={setIsServiceImageCarouselOpen}
                  setSelectedServiceIdForCarousel={
                    setSelectedServiceIdForCarousel
                  }
                  setSelectedServiceTypeForCarousel={
                    setSelectedServiceTypeForCarousel
                  }
                  handleEditParkingServiceMetadataClick={
                    handleEditParkingServiceMetadataClick
                  }
                  handleUpdateParkingServiceParkingInfoClick={
                    handleUpdateParkingServiceParkingInfoClick
                  }
                  handleDeleteParkingServiceClick={
                    handleDeleteParkingServiceClick
                  }
                  handleUpdateParkingServiceHeaderInfoClick={
                    handleUpdateParkingServiceHeaderInfoClick
                  }
                  handleUpdateParkingServiceParkingRateClick={
                    handleUpdateParkingServiceParkingRateClick
                  }
                />
              );
            })}
          {allEVSEsData.length > 0 &&
            allEVSEsData.map((service) => {
              return (
                <ChargingServiceInformationCard
                  serviceId={service.self.id}
                  setIsServiceImageCarouselOpen={setIsServiceImageCarouselOpen}
                  setSelectedServiceIdForCarousel={
                    setSelectedServiceIdForCarousel
                  }
                  setSelectedServiceTypeForCarousel={
                    setSelectedServiceTypeForCarousel
                  }
                  handleEditChargingServiceMetadataClick={
                    handleEditChargingServiceMetadataClick
                  }
                  handleAddEVSEAndConnectorsClick={
                    handleAddEVSEAndConnectorsClick
                  }
                  handleUpdateEVSEAndConnectorsClick={
                    handleUpdateEVSEAndConnectorsClick
                  }
                  handleDeleteEVSEAndConnectorsClick={
                    handleDeleteEVSEAndConnectorsClick
                  }
                  handleAddChargingConnectorClick={
                    handleAddChargingConnectorClick
                  }
                  handleDeleteChargingServiceClick={
                    handleDeleteChargingServiceClick
                  }
                  handleUpdateChargingServiceHeaderInfoClick={
                    handleUpdateChargingServiceHeaderInfoClick
                  }
                />
              );
            })}
          {fuelingServices.length > 0 &&
            fuelingServices.map((service) => {
              return (
                <FuelingServiceInformationCard
                  serviceId={service.id}
                  setIsServiceImageCarouselOpen={setIsServiceImageCarouselOpen}
                  setSelectedServiceIdForCarousel={
                    setSelectedServiceIdForCarousel
                  }
                  setSelectedServiceTypeForCarousel={
                    setSelectedServiceTypeForCarousel
                  }
                  handleEditFuelingServiceMetadataClick={
                    handleEditFuelingServiceMetadataClick
                  }
                  handleUpdateFuelingServiceFuelingInfoClick={
                    handleUpdateFuelingServiceFuelingInfoClick
                  }
                  handleDeleteFuelingServiceClick={
                    handleDeleteFuelingServiceClick
                  }
                  handleUpdateFuelingServiceHeaderInfoClick={
                    handleUpdateFuelingServiceHeaderInfoClick
                  }
                />
              );
            })}
          {maintenanceServices.length > 0 &&
            maintenanceServices.map((service) => {
              return (
                <MaintenanceServiceInformationCard
                  serviceId={service.id}
                  setIsServiceImageCarouselOpen={setIsServiceImageCarouselOpen}
                  setSelectedServiceIdForCarousel={
                    setSelectedServiceIdForCarousel
                  }
                  setSelectedServiceTypeForCarousel={
                    setSelectedServiceTypeForCarousel
                  }
                  handleEditMaintenanceServiceMetadataClick={
                    handleEditMaintenanceServiceMetadataClick
                  }
                  handleDeleteMaintenanceServiceClick={
                    handleDeleteMaintenanceServiceClick
                  }
                  handleUpdateMaintenanceServiceHeaderInfoClick={
                    handleUpdateMaintenanceServiceHeaderInfoClick
                  }
                />
              );
            })}
          {inspectionServices.length > 0 &&
            inspectionServices.map((service) => {
              return (
                <InspectionServiceInformationCard
                  serviceId={service.id}
                  setIsServiceImageCarouselOpen={setIsServiceImageCarouselOpen}
                  setSelectedServiceIdForCarousel={
                    setSelectedServiceIdForCarousel
                  }
                  setSelectedServiceTypeForCarousel={
                    setSelectedServiceTypeForCarousel
                  }
                  handleEditInspectionServiceMetadataClick={
                    handleEditInspectionServiceMetadataClick
                  }
                  handleDeleteInspectionServiceClick={
                    handleDeleteInspectionServiceClick
                  }
                  handleUpdateInspectionServiceHeaderInfoClick={
                    handleUpdateInspectionServiceHeaderInfoClick
                  }
                />
              );
            })}
        </div>
      </div>
    </>
  );
};

export default PlaceInformationPage;
